import { dayjs } from 'utils/DateUtils';
import jwt_decode from 'jwt-decode';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { Formik } from 'formik';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import ButtonLoading from '@mui/lab/LoadingButton';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/VisibilityOutlined';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import IconButton from '@mui/material/IconButton';
import { LoginValidationSchema } from 'config/FormValidaton/AuthValidationSchema';
import { BasicAlert } from 'components/AlertComponent';
import { useGetRoleAccessToken, useLogin } from 'services/v1/Auth';
import {
  RoleType,
  setRememberMe,
  setRole,
  setSelectedTenant,
  setTenants,
  setProfile,
  setRoles,
  setUserBehaviourRole,
  setAccessToken,
  setSessionStatus,
  logout,
  addBrowserLoginHistory,
} from 'store/reducer/authReducer';
import { removeAuthHeaderToken, setAuthHeaderToken, setAuthTenantId } from 'libs/Axios';
import { getErrorMessage } from 'utils/Error';
import { SESSION_STATUS_CONSTANT } from 'constant/SessionStatusConstant';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  useGetMyPersonalizationLogin,
  useGetTenantConfigDataMutation,
} from 'services/v1/Tenant/ConfigurationService';
import { Link } from '@mui/material';
import { FOOTER_MENU_LINK, FOOTER_MENU_OPTIONS } from 'constant/NavigationConstant';
import { setTenantNavigation } from 'store/reducer/uiReducer';
import { setConfig, setSetupStatus } from 'store/reducer/settingReducer';
import { DASHBOARD_TYPE_CONSTANT } from 'constant/DashboardTypeConstant';
import { TENANT_CONFIGURATION_KEY, TENANT_INITIAL_SETUP_STATUS } from 'constant/TenantConfigConstant';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { getUserRoleBehaviour } from './SelectRolePage';

interface formValues {
  userName: string;
  password: string;
}

const formInitialValues: formValues = {
  userName: '',
  password: '',
};

const AROTARGET_TEAM_EMAIL_MAP: Record<string, string> = {
  'charles wilson': 'charlesv@arotarget.com',
  'eleanor cook': 'eleanor@arotarget.com',
  'garth abrahamson': 'garth@arotarget.com',
  'oliver smith': 'oliver@arotarget.com',
  'pauline jones': 'pauline@arotarget.com',
  'sally roberts': 'sally@arotarget.com',
  'william townsend': 'william@arotarget.com',
  'rachel brown': 'rachel@arotarget.com',
};

const gridBannerStyle = {
  backgroundImage: `url('/images/auth-banner.jpg')`,
  backgroundRepeat: 'no-repeat',
  backgroundBlendMode: 'color',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  height: '100%',
};

const greetingTextBoldStyle = {
  marginBottom: 2,
  fontWeight: '500',
};

const gridContentContainerStyle = {
  backgroundColor: '#F8FAFF',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentContainerStyle = {
  my: 3,
  mx: 4,
  px: 4,
  py: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: '#fff',
  borderRadius: '7px',
  boxShadow: '0px 1px 6px #C8CFE2',
};

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authReducer = useSelector((state: RootState) => state.auth);
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const srcParam = searchParams.get('src');
  const personParam = searchParams.get('person');

  const prefilledEmail =
    srcParam === 'arotarget_team' && personParam
      ? AROTARGET_TEAM_EMAIL_MAP[personParam.trim().toLowerCase()] ?? ''
      : '';

  const loginInitialValues: formValues = {
    ...formInitialValues,
    userName: prefilledEmail,
  };

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError, error } = useLogin();
  const { mutateAsync: refetchPersonalization, isLoading: isLoadingPersonalization } = useGetMyPersonalizationLogin();
  const { mutateAsync: getTenantConfig, isLoading: isFetchingTenantConfig } = useGetTenantConfigDataMutation();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [isAgreeWithToc, setIsAgreeWithToc] = useState<boolean>(false);
  const [disabledCheckbox, setDisabledCheckbox] = useState<boolean>(false);

  const { mutateAsync: getRoleAccessToken, isLoading: isLoadingRoleAccessToken } = useGetRoleAccessToken();

  const handleSubmit = async (data: formValues) => {
    if (showPassword) setShowPassword(false);
    mutate(data, {
      onSuccess: async (response, variables, context) => {
        const sessionPersistData = {
          rememberMe: false,
          expiredDate: dayjs().add(120, 'minutes').toISOString(),
        };

        dispatch(setRememberMe(sessionPersistData));
        dispatch(setTenants(response.data.tenants));
        dispatch(setRoles(response.data.roles));
        setAuthHeaderToken(response.headers.authorization);

        const decodeToken = jwt_decode(response.headers.authorization);

        // @ts-ignore
        dispatch(setProfile({ name: response.data.fullName, id: decodeToken?.id || null }));

        let skipFetchPersonalization = false;
        let initialSetupRedirect = false;
        const lastAccessedTenant =
          response.data.tenants.find((tenant) => tenant.tenant.isLastAccessed) ?? response.data.tenants[0];
        const autoSelectedTenant =
          response.data.tenants.length === 1 ? response.data.tenants[0] : lastAccessedTenant;

        if (autoSelectedTenant) {
          setAuthTenantId(autoSelectedTenant.tenant.id);
          const res = await getRoleAccessToken({
            roleName: autoSelectedTenant.tenant.roleName,
            tenantId: autoSelectedTenant.tenant.id,
          });
          const roleAccessToken = res.data.data;
          setAuthHeaderToken(roleAccessToken);
          dispatch(setAccessToken(roleAccessToken));
          dispatch(setRole(autoSelectedTenant.roleType));
          dispatch(setSelectedTenant(autoSelectedTenant));
          const roleBehaviour = getUserRoleBehaviour(
            autoSelectedTenant.roleType as RoleType,
            autoSelectedTenant,
          ) as RoleType;
          dispatch(setUserBehaviourRole(roleBehaviour));

          if (autoSelectedTenant.tenant.dashboardType === DASHBOARD_TYPE_CONSTANT.DASHBOARD_TENANT) {
            const tenantConfig = await getTenantConfig();
            const initialSetupStatus = tenantConfig.data?.find(
              (config) => config.id === TENANT_CONFIGURATION_KEY.INITIAL_SETUP_STATUS,
            )?.value;

            initialSetupStatus && dispatch(setSetupStatus(initialSetupStatus));
            dispatch(setConfig(tenantConfig.data || []));

            if (initialSetupStatus === TENANT_INITIAL_SETUP_STATUS.INITIAL) {
              initialSetupRedirect = true;
            }
          }

          if (autoSelectedTenant.roleType === 'Builder') {
            skipFetchPersonalization = true;
          }
          queryClient.resetQueries();
        }

        dispatch(setSessionStatus(SESSION_STATUS_CONSTANT.ACTIVE));
        if (!skipFetchPersonalization) {
          await refetchPersonalization();
        }
        dispatch(
          addBrowserLoginHistory({
            timestamp: dayjs().toISOString(),
            // @ts-ignore
            userFullName: response.data.fullName,
          }),
        );
        if (initialSetupRedirect) {
          navigate(PATH_CONSTANT.AUTH.INITIAL_SETUP, { replace: true });
          return;
        }
        redirectParam ? navigate(redirectParam, { replace: true }) : navigate('/', { replace: true });
      },
      onError: (error) => {
        removeAuthHeaderToken();
      },
    });
  };
  const handleConfirmPasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (authReducer.accessToken) {
      dispatch(setTenantNavigation(false));
      dispatch(logout());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authReducer.browserLoginHistory?.length > 0) {
      setIsAgreeWithToc(true);
      setDisabledCheckbox(true);
    }
  }, [authReducer.browserLoginHistory]);

  const isSubmitting = isLoadingPersonalization || isLoadingRoleAccessToken || isFetchingTenantConfig;

  return (
    <Grid container component='main' sx={{ height: '100vh' }}>
      <CssBaseline />

      <Grid
        item
        display='flex'
        flexDirection='column'
        alignItems='center'
        paddingBottom={3}
        justifyContent='space-between'
        md={5}
        sx={{ ...gridBannerStyle, display: { xs: 'none', md: 'flex' } }}
      >
        <Box width='100%' justifyContent='center' display='flex' sx={{ marginTop: 25 }}>
          <Stack direction='column' alignItems='flex-start' width='75%'>
            <Typography component='h2' variant='h5' color='#fff' sx={greetingTextBoldStyle}>
              {t('PAGE.LOGIN.WELCOME_TITLE')}
            </Typography>

            <Typography component='p' variant='subtitle1' color='#fff'>
              {t('PAGE.LOGIN.WELCOME_DESCRIPTION')}
            </Typography>
          </Stack>
        </Box>
        <Box width='100%' justifyContent='center' display='flex'>
          <Stack
            direction='row'
            width='75%'
            alignItems='center'
            justifyContent='flex-start'
            gap={3}
            height={80}
            divider={<Divider orientation='vertical' flexItem color='#fff' />}
          >
            <Stack direction='row' gap={1} mt={2}>
              <div>
                <img
                  src='/images/jazanz_logo.jpg'
                  alt='Jas ANZ Logo'
                  style={{
                    width: 'auto',
                    height: 60,
                  }}
                />
              </div>
              <div>
                <img
                  src='/images/iso_9001_certified.png'
                  alt='ISO 9001 Certified'
                  style={{
                    background: 'white',
                    width: 'auto',
                    height: 60,
                  }}
                />
              </div>
            </Stack>
            <Stack>
              <Stack direction='row' spacing={2}>
                {FOOTER_MENU_OPTIONS.map((menu) => (
                  <Link
                    key={menu.link}
                    href={menu.link}
                    underline='hover'
                    target='_blank'
                    sx={{
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 'bold',
                      '&:hover': {
                        color: '#fff',
                      },
                    }}
                  >
                    {menu.label}
                  </Link>
                ))}
              </Stack>
              <Typography component='p' variant='subtitle1' color='#fff' sx={{ fontSize: 10, marginTop: 1 }}>
                {`©️ ${dayjs().year()} ARO Forecasting Pty Ltd All Rights Reserved.`}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Grid>

      <Grid item xs={12} md={7} sx={gridContentContainerStyle}>
        <Box width={500} sx={contentContainerStyle}>
          <Box width={400} display='flex' justifyContent='space-between'>
            <Typography
              component='h5'
              variant='h5'
              color='#394798'
              sx={{
                marginBottom: 3,
              }}
            >
              {t('PAGE.LOGIN.FORM_TITLE')}
            </Typography>

            <div>
              <img
                src='/images/main-logo.svg'
                alt='logo'
                style={{
                  width: '100px',
                  height: 'auto',
                }}
              />
            </div>
          </Box>
          <Box width={400}>
            {!isError && (
              <Typography component='h4' variant='h4' color='#2C2A29'>
                {t('PAGE.LOGIN.FORM_SUB_TITLE')}
              </Typography>
            )}
            <Collapse in={isError}>
              {isError && <BasicAlert type='error' title={getErrorMessage(error)} />}
            </Collapse>

            <Formik initialValues={loginInitialValues} onSubmit={handleSubmit} validationSchema={LoginValidationSchema}>
              {({ handleChange, handleBlur, handleSubmit, touched, errors, values }) => (
                <form
                  onSubmit={handleSubmit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                >
                  <Stack mt={2}>
                    <Typography component='p' variant='input-label' color='#98A2AE'>
                      {t('PAGE.LOGIN.FORM_LOGIN_INPUT_EMAIL_LABEL')}
                    </Typography>
                    <TextField
                      autoFocus
                      margin='dense'
                      value={values.userName}
                      disabled={isLoading || isSubmitting}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.userName && !!errors.userName}
                      helperText={touched.userName && errors.userName}
                      fullWidth
                      placeholder='Enter your email address'
                      name='userName'
                      variant='outlined'
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          transition: 'box-shadow 0.2s ease',
                          '&.Mui-focused': {
                            boxShadow: '0 0 0 3px rgba(58, 187, 147, 0.15)',
                          },
                        },
                      }}
                    />
                  </Stack>
                  <Stack>
                    <Typography component='p' variant='input-label' color='#98A2AE'>
                      {t('PAGE.LOGIN.FORM_LOGIN_INPUT_PASSWORD_LABEL')}
                    </Typography>
                    <TextField
                      value={values.password}
                      disabled={isLoading || isSubmitting}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && !!errors.password}
                      helperText={touched.password && errors.password}
                      margin='dense'
                      fullWidth
                      placeholder='Enter your password'
                      name='password'
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton onClick={handleConfirmPasswordToggle} size='small'>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      variant='outlined'
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          transition: 'box-shadow 0.2s ease',
                          '&.Mui-focused': {
                            boxShadow: '0 0 0 3px rgba(58, 187, 147, 0.15)',
                          },
                        },
                      }}
                    />
                  </Stack>
                  <Box display='flex' alignItems='center' justifyContent='space-between' mt={1}>
                    <FormControlLabel
                      sx={{ alignItems: 'flex-start', mt: 0.5 }}
                      control={
                        <Checkbox
                          value='remember'
                          disabled={disabledCheckbox}
                          checked={isAgreeWithToc}
                          onChange={(e) => {
                            setIsAgreeWithToc(e.target.checked);
                          }}
                          sx={{
                            pt: 0.5,
                            color: '#3ABB93',
                            opacity: disabledCheckbox ? 0.7 : 1,
                            '&.Mui-checked': { color: '#3ABB93' },
                          }}
                        />
                      }
                      label={
                        <Typography component='p' variant='body2' color='text.secondary' sx={{ fontSize: 13, lineHeight: 1.6, mt: 0.5 }}>
                          I have read and accept the{' '}
                          <Link href={FOOTER_MENU_LINK.TERMS_OF_USE} target='_blank' color='primary'>
                            Terms of Use
                          </Link>{' '}
                          and{' '}
                          <Link href={FOOTER_MENU_LINK.PRIVACY_POLICY} target='_blank' color='primary'>
                            Privacy Policy
                          </Link>
                        </Typography>
                      }
                    />
                  </Box>

                  <ButtonLoading
                    type='submit'
                    fullWidth
                    loading={isLoading || isSubmitting}
                    loadingPosition='start'
                    onClick={() => handleSubmit()}
                    variant='main-login'
                    sx={{
                      mt: 2,
                      mb: 2,
                    }}
                    disabled={!LoginValidationSchema.isValidSync(values) || !isAgreeWithToc}
                  >
                    {isLoading || isSubmitting ? 'Signing in...' : t('PAGE.LOGIN.FORM_BUTTON_LOGIN')}
                  </ButtonLoading>
                </form>
              )}
            </Formik>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
